import config, {
  envIsLocal,
  envIsPublicCloud,
  urlPrefixed,
} from '@wandb/common/config';
import * as globals from '@wandb/common/css/globals.styles';
import {useBooleanState, useSyncedState} from '@wandb/common/util/hooks';
import React, {memo, useCallback, useMemo} from 'react';
import {Checkbox, Image} from 'semantic-ui-react';
import styled from 'styled-components';

import {useAutoRefreshSettings} from '../../../containers/AutoRefreshSettings';
import {
  useServerInfoQuery,
  useViewerPhotoUrlQuery,
} from '../../../generated/graphql';
import {auth} from '../../../setup';
import {useViewer} from '../../../state/viewer/hooks';
import {
  setViewerUsingAdminPrivileges,
  useAdminModeActive,
  viewerUsingAdminPrivileges,
  viewingAs,
} from '../../../util/admin';
import history from '../../../util/history';
import {
  adminHome,
  adminUsers,
  homePage,
  profilePage,
  settings,
  subscriptions,
} from '../../../util/urls';
import {useBetaFeature} from '../../../util/useBetaFeature';
import {removeNullUndefinedOrFalse} from '../../../util/utility';
import {
  IconCreditCardPayment,
  IconDashboardBlackboard,
  IconDocument,
  IconImpersonateMaskAlt,
  IconJobCalculator,
  IconLogOut,
  IconMagicWandStick,
  IconRandomizeAlt,
  IconSettingsParameters,
  IconShareWith,
  IconVersionsLayers,
} from '../../Icon';
import {ImpersonateUserModal} from '../../ImpersonateUserModal';
import {LoginButton} from '../../LoginButton';
import UsageSimulationModal from '../../UsageSimulationModal';
import NavMenu, {NavMenuSection} from './NavMenu';

type ProfileMenuProps = {
  isSignup?: boolean;
};

const ProfileMenuComp: React.FC<ProfileMenuProps> = React.memo(({isSignup}) => {
  const viewer = useViewer();

  const {
    state: menuOpen,
    setTrue: openMenu,
    setFalse: closeMenu,
  } = useBooleanState(false);
  const {
    state: limitConfigOpen,
    setTrue: openLimitConfig,
    setFalse: closeLimitConfig,
  } = useBooleanState(false);
  const {
    state: impersonateModalOpen,
    setTrue: openImpersonateModal,
    setFalse: closeImpersonateModal,
  } = useBooleanState(false);

  const {data: viewerPhotoURLData} = useViewerPhotoUrlQuery({
    fetchPolicy: `network-only`,
  });
  const viewerPhotoURL = viewerPhotoURLData?.viewer?.photoUrl;

  const sections: NavMenuSection[] = useProfileMenuSections({
    openLimitConfig,
    openImpersonateModal,
    isSignup,
  });

  if (viewer == null) {
    return (
      <div className="viewer-dropdown">
        <div className="logged-out-menu">
          <LoginButton />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="viewer-dropdown">
        <NavMenu
          open={menuOpen}
          onOpen={openMenu}
          onClose={closeMenu}
          trigger={
            <Container data-test="nav-profile">
              <Avatar
                className="nav-propic"
                src={viewerPhotoURL ?? viewer.photoUrl}
                avatar
              />
            </Container>
          }
          sections={sections}
        />
      </div>
      {impersonateModalOpen && (
        <ImpersonateUserModal
          onClose={closeImpersonateModal}
          onCreate={() => history.push(homePage())}
          onClick={e => {
            e.stopPropagation();
          }}
        />
      )}
      {limitConfigOpen && <UsageSimulationModal onClose={closeLimitConfig} />}
    </>
  );
});

const ProfileMenu = memo(ProfileMenuComp);

export default ProfileMenu;

const Container = styled.div`
  border-radius: 100%;
  height: ${globals.SEARCH_NAV_HEIGHT};
  width: 62px;
  cursor: pointer;
  position: relative;
  display: inline-block;
  outline: 0;
  text-align: left;
  -webkit-tap-highlight-color: transparent;

  transition: padding 0.15s;
  padding: 10px 20px 10px 2px;
  &:hover {
    padding: 8px 18px 8px 0px;
  }
`;

const Avatar = styled(Image)`
  &&& {
    background-color: ${globals.WHITE};
  }
`;

const SWITCH_WIDTH = 34;
const SWITCH_HEIGHT = 20;

const Switch = styled(Checkbox).attrs({toggle: true})`
  &&&&& {
    width: ${SWITCH_WIDTH}px;
    min-height: ${SWITCH_HEIGHT}px;
    label {
      min-height: ${SWITCH_HEIGHT}px;
    }

    label:before {
      background-color: ${p =>
        p.checked ? globals.TEAL_LIGHT : globals.GRAY_400} !important;
      width: ${SWITCH_WIDTH}px;
      height: ${SWITCH_HEIGHT}px;
    }

    label:after {
      box-shadow: none;
      width: ${SWITCH_HEIGHT - 2}px;
      height: ${SWITCH_HEIGHT - 2}px;
      top: 1px;
      left: ${p => (p.checked ? SWITCH_WIDTH - SWITCH_HEIGHT + 1 : 1)}px;
    }
  }
`;

type UseProfileMenuSectionsParams = {
  openLimitConfig: () => void;
  openImpersonateModal: () => void;
  isSignup?: boolean;
};

function useProfileMenuSections({
  openLimitConfig,
  openImpersonateModal,
  isSignup = false,
}: UseProfileMenuSectionsParams): NavMenuSection[] {
  const viewer = useViewer();
  const admin = useAdminModeActive();

  const {autoRefreshEnabled, setAutoRefreshEnabled} = useAutoRefreshSettings();
  const {state: autoRefreshEnabledLocal, setState: setAutoRefreshEnabledLocal} =
    useSyncedState({
      stateToSyncWith: autoRefreshEnabled,
      setStateToSyncWith: setAutoRefreshEnabled,
    });
  const toggleAutoRefresh = useCallback(() => {
    setAutoRefreshEnabledLocal(!autoRefreshEnabledLocal);
  }, [autoRefreshEnabledLocal, setAutoRefreshEnabledLocal]);

  const {
    state: viewerUsingAdminPrivilegesLocal,
    setState: setViewerUsingAdminPrivilegesLocal,
  } = useSyncedState({
    stateToSyncWith: viewerUsingAdminPrivileges(),
    setStateToSyncWith: setViewerUsingAdminPrivileges,
  });
  const toggleViewerUsingAdminPrivileges = useCallback(() => {
    setViewerUsingAdminPrivilegesLocal(!viewerUsingAdminPrivilegesLocal);
    setTimeout(() => window.location.reload(), 500);
  }, [viewerUsingAdminPrivilegesLocal, setViewerUsingAdminPrivilegesLocal]);
  const weave1BetaFeature = useBetaFeature(`weave1`);

  const serverInfoQuery = useServerInfoQuery();
  const isGoogleIap = serverInfoQuery.data?.serverInfo?.isGoogleIap;

  const canLogout = !config.AUTH_STUB_JWT || config.AUTH_STUB_JWT === 'false';

  return useMemo(
    () =>
      removeNullUndefinedOrFalse([
        !isSignup && {
          items: removeNullUndefinedOrFalse([
            {
              icon: <IconShareWith />,
              label: `Profile`,
              to: profilePage(viewer?.username ?? ``),
            },

            {
              icon: <IconSettingsParameters />,
              label: `User settings`,
              to: settings(),
            },

            {
              icon: <IconRandomizeAlt />,
              label: `Auto-refresh`,
              onClick: toggleAutoRefresh,
              closeMenuOnClick: false,
              rightSide: (
                <Switch
                  checked={autoRefreshEnabledLocal}
                  data-test="auto-refresh-checkbox"
                />
              ),
            },

            envIsPublicCloud && {
              icon: <IconCreditCardPayment />,
              label: `Subscriptions`,
              to: subscriptions(),
            },
          ]),
        },

        !isSignup &&
          viewer?.admin && {
            header: `W&B admin tools`,
            backgroundColor: globals.GRAY_50,
            items: removeNullUndefinedOrFalse([
              envIsPublicCloud && {
                icon: <IconDocument />,
                label: `Admin pages`,
                to: adminHome(),
              },

              !envIsLocal && {
                icon: <IconMagicWandStick />,
                label: `Admin powers`,
                onClick: toggleViewerUsingAdminPrivileges,
                closeMenuOnClick: false,
                rightSide: <Switch checked={viewerUsingAdminPrivilegesLocal} />,
              },

              !envIsLocal && {
                icon: <IconVersionsLayers />,
                label: `Enable Weave1`,
                onClick: weave1BetaFeature.toggleBetaFeature,
                closeMenuOnClick: false,
                rightSide: <Switch checked={weave1BetaFeature.isEnabled} />,
              },

              // !envIsLocal && {
              //   icon: <IconShowVisible />,
              //   label: `Admin view`,
              //   to: adminHome(),
              // },
              admin &&
                !envIsLocal && {
                  icon: <IconJobCalculator />,
                  label: `Simulate usage`,
                  onClick: openLimitConfig,
                },

              (!envIsLocal || !!viewingAs()) && {
                icon: <IconImpersonateMaskAlt />,
                label: `Use as...`,
                onClick: openImpersonateModal,
              },

              envIsLocal && {
                icon: <IconSettingsParameters />,
                label: `System settings`,
                href: urlPrefixed(`/system-admin`, false),
              },

              envIsLocal && {
                icon: <IconShareWith />,
                label: `Users`,
                to: adminUsers(),
              },

              envIsLocal && {
                icon: <IconDashboardBlackboard />,
                label: `Activity dashboard`,
                href: urlPrefixed(`/admin/usage`, false),
              },
            ]),
          },

        canLogout && {
          items: removeNullUndefinedOrFalse([
            {
              icon: <IconLogOut />,
              label: `Log out`,
              onClick: () => auth.logout(),
              ...(isGoogleIap
                ? {
                    disabled: true,
                    tooltipText: `This instance is using Google IAP for authentication, so you'll need to log out of your Google account to log out of wandb.`,
                  }
                : {}),
            },
          ]),
        },
      ]),
    [
      isSignup,
      viewer?.username,
      viewer?.admin,
      toggleAutoRefresh,
      autoRefreshEnabledLocal,
      toggleViewerUsingAdminPrivileges,
      viewerUsingAdminPrivilegesLocal,
      weave1BetaFeature.toggleBetaFeature,
      weave1BetaFeature.isEnabled,
      admin,
      openLimitConfig,
      openImpersonateModal,
      canLogout,
      isGoogleIap,
    ]
  );
}
